import React, {useState} from 'react';
import {
  Box,
  Typography,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Users,
  Settings,
  Building2,
  LogOut,
  ArrowLeft,
  Building,
  Search,
  X,
} from 'lucide-react';
import {
  useAuthInfo,
  useLogoutFunction,
  useRedirectFunctions,
} from '@propelauth/react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import Intercom from '@intercom/messenger-js-sdk';
import {truncate} from '../../util';

const styles = {
  header: {
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '150%' /* 21px */,
    letterSpacing: '-0.14px',
  },
  sectionContainer: {
    minHeight: '80px',
  },
  scrollableContent: {
    maxHeight: '320px',
    overflowY: 'auto',
  },
  navItem: {
    display: 'flex',
    padding: '8px 12px',
    alignItems: 'center',
    gap: '8px',
    alignSelf: 'stretch',
    cursor: 'pointer',
    borderRadius: '8px',
    marginBottom: '6px',
    '&:hover': {
      backgroundColor: '#F2F4F4',
    },
  },
  navItemActive: {
    backgroundColor: '#F2F4F4',
  },
  navItemText: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '150%' /* 21px */,
    letterSpacing: '-0.14px',
    color: '#162523',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  backButton: {
    display: 'flex',
    padding: '12px',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    borderRadius: '8px',
    marginTop: '24px',
    '&:hover': {
      backgroundColor: '#F2F4F4',
    },
  },
  backButtonText: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '150%',
    letterSpacing: '-0.14px',
    color: '#162523',
  },
  searchInput: {
    '& .MuiOutlinedInput-root': {
      fontSize: '14px',
      borderRadius: '8px',
      backgroundColor: '#F9FAFA',
      '& fieldset': {
        borderColor: '#E1E5E4',
      },
      '&:hover fieldset': {
        borderColor: '#C5CBCA',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgb(52, 162, 144)',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
    },
  },
  clearButton: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      opacity: 0.7,
    },
  },
};

const SettingsNavBar = () => {
  const {accessHelper} = useAuthInfo();
  const [activeItem, setActiveItem] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const logoutFn = useLogoutFunction();
  const {redirectToLoginPage} = useRedirectFunctions();
  const {parentOrg, clientOrgs, hasParentOrg} = useSelector(
    (state) => state.session
  );
  const location = useLocation();
  const navigate = useNavigate();

  const isAccountSettingsActive = location.pathname === '/settings/my-account';
  const isOrganizationUsersActive =
    location.pathname === '/settings/organization/users';
  const isOrganizationClientsActive =
    location.pathname === '/settings/organization/clients';

  const isCompanyActive = (org) => {
    const slug = org.orgName.toLowerCase().replace(/\s+/g, '-');
    const expectedPathPrefix = `/settings/${slug}`;
    return (
      location.pathname === expectedPathPrefix ||
      location.pathname.startsWith(`${expectedPathPrefix}/`)
    );
  };

  const hasParentOrgAdminAccess = () => {
    if (!parentOrg) return false;

    return (
      accessHelper.isRole(parentOrg.orgId, 'InScope Admin') ||
      accessHelper.isRole(parentOrg.orgId, 'Owner')
    );
  };

  const shouldShowOrganizationSection = () => {
    if (!parentOrg) return false;

    return (
      hasParentOrgAdminAccess() ||
      accessHelper.hasPermission(parentOrg.orgId, 'can_view_pso_orgs')
    );
  };

  const shouldShowCompaniesSection = () => {
    return clientOrgs.some(
      (org) =>
        accessHelper.isRole(org.orgId, 'InScope Admin') ||
        accessHelper.isRole(org.orgId, 'Admin') ||
        accessHelper.isRole(org.orgId, 'Owner')
    );
  };

  // Determine which sections to show based on role
  const showOrganization = shouldShowOrganizationSection();
  const showCompanies = shouldShowCompaniesSection();
  const showAccount = true;
  const showAllUsers = hasParentOrgAdminAccess();

  const handleItemClick = async (itemId) => {
    setActiveItem(itemId);

    if (itemId === 'logout') {
      Intercom('shutdown');
      await logoutFn(false);
      redirectToLoginPage({postLoginRedirectUrl: window.location.href});
    } else if (itemId === 'users') {
      navigate('/settings/organization/users');
    } else if (itemId === 'clients') {
      navigate('/settings/organization/clients');
    }
  };

  const handleCompanyClick = (org) => {
    const slug = org.orgName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/settings/${slug}/users`);
  };

  return (
    <Box
      sx={{
        width: '286px',
        backgroundColor: 'white',
        borderRight: '1px solid #E1E5E4',
        padding: '24px',
        paddingTop: '90px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{flexGrow: 1, overflow: 'auto', minHeight: 0}}>
        {/* Organization Section - Only show for Owner or Inscope Admin */}
        {showOrganization && (
          <>
            <Box sx={styles.sectionContainer}>
              <Typography sx={styles.header}>
                {parentOrg?.orgName || 'Organization'}
              </Typography>

              <Box sx={{marginTop: 2}}>
                {showAllUsers && (
                  <Box
                    sx={{
                      ...styles.navItem,
                      ...(isOrganizationUsersActive
                        ? styles.navItemActive
                        : {}),
                    }}
                    onClick={() => handleItemClick('users')}
                  >
                    <Users size={18} color="#162523" />
                    <Typography sx={styles.navItemText}>All users</Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    ...styles.navItem,
                    ...(isOrganizationClientsActive
                      ? styles.navItemActive
                      : {}),
                  }}
                  onClick={() => handleItemClick('clients')}
                >
                  <Building size={18} color="#162523" />
                  <Typography sx={styles.navItemText}>All clients</Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* Companies Section - Only show for Admin, Owner, or Inscope Admin */}
        {showCompanies && (
          <>
            {showOrganization && (
              <Divider sx={{backgroundColor: '#E1E5E4', marginY: 2}} />
            )}
            <Box sx={styles.sectionContainer}>
              <Typography sx={styles.header}>
                {hasParentOrg ? 'Clients' : 'Companies'}
              </Typography>

              <TextField
                fullWidth
                placeholder="Search companies..."
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                sx={{...styles.searchInput, marginTop: 2}}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color="#7A8886" />
                    </InputAdornment>
                  ),
                  endAdornment: companySearch && (
                    <InputAdornment position="end">
                      <Box
                        sx={styles.clearButton}
                        onClick={() => setCompanySearch('')}
                      >
                        <X size={16} color="#7A8886" />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{...styles.scrollableContent, marginTop: 2}}>
                {clientOrgs
                  .filter(
                    (org) =>
                      (accessHelper.isRole(org.orgId, 'InScope Admin') ||
                        accessHelper.isRole(org.orgId, 'Admin') ||
                        accessHelper.isRole(org.orgId, 'Owner')) &&
                      org.orgName
                        .toLowerCase()
                        .includes(companySearch.toLowerCase())
                  )
                  .map((org) => (
                    <Tooltip
                      key={org.orgId}
                      title={
                        <Typography
                          sx={{p: '8px'}}
                          fontSize={13}
                          fontWeight={500}
                        >
                          {org.orgName}
                        </Typography>
                      }
                      placement="right"
                      enterDelay={0}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            borderRadius: '4px',
                            backgroundColor: '#27353A',
                            py: 0,
                            px: 0.2,
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          ...styles.navItem,
                          ...(isCompanyActive(org) ? styles.navItemActive : {}),
                        }}
                        onClick={() => handleCompanyClick(org)}
                      >
                        <Box sx={{flexShrink: 0}}>
                          <Building2 size={18} color="#162523" />
                        </Box>
                        <Typography sx={styles.navItemText}>
                          {truncate(org.orgName, 30)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
              </Box>
            </Box>
          </>
        )}

        {/* Divider before Account section */}
        {(showOrganization || showCompanies) && showAccount && (
          <Divider sx={{backgroundColor: '#E1E5E4', marginY: 2}} />
        )}

        {/* Account Section - Always show */}
        {showAccount && (
          <Box sx={styles.sectionContainer}>
            <Typography sx={styles.header}>Account</Typography>

            <Box sx={{marginTop: 2}}>
              <Box
                sx={{
                  ...styles.navItem,
                  ...(isAccountSettingsActive ? styles.navItemActive : {}),
                }}
                onClick={() => navigate('/settings/my-account')}
              >
                <Settings size={18} color="#162523" />
                <Typography sx={styles.navItemText}>Settings</Typography>
              </Box>

              <Box
                sx={{
                  ...styles.navItem,
                  ...(activeItem === 'logout' ? styles.navItemActive : {}),
                }}
                onClick={() => handleItemClick('logout')}
              >
                <LogOut size={18} color="#162523" />
                <Typography sx={styles.navItemText}>Log out</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Back Button */}
      <Box sx={styles.backButton} onClick={() => navigate('/')}>
        <ArrowLeft size={18} color="#162523" />
        <Typography sx={styles.backButtonText}>Back to dashboard</Typography>
      </Box>
    </Box>
  );
};

export default SettingsNavBar;
